import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  dkConfirmEfDkSettlement,
  fetchEfDkSettlements,
  finalConfirmEfDkSettlement,
  lockEfDkSettlement,
  recomputeEfDkSettlement,
  reviseEfDkSettlement,
} from './api';
import type { EfDkSettlementDto } from './types';

const KEY = 'ef-dk-settlement' as const;

/** Batch-scoped list of settlements (the page reads the latest header row). */
export const useEfDkSettlements = (batchId: string | undefined) => {
  return useQuery<EfDkSettlementDto[], ApiClientError>({
    queryKey: [KEY, 'list', batchId],
    queryFn: () => fetchEfDkSettlements(batchId!),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

/** POST compute/recompute for the batch. */
export const useRecomputeEfDkSettlement = (batchId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<EfDkSettlementDto, ApiClientError, void>({
    mutationFn: () => recomputeEfDkSettlement(batchId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'list', batchId] }),
  });
};

/** Shared shape for the four id-scoped transitions (dk/final/lock/revise). */
const useTransition = (
  batchId: string | undefined,
  fn: (id: string) => Promise<EfDkSettlementDto>,
) => {
  const qc = useQueryClient();
  return useMutation<EfDkSettlementDto, ApiClientError, string>({
    mutationFn: (id) => fn(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'list', batchId] }),
  });
};

export const useDkConfirmEfDkSettlement = (batchId: string | undefined) =>
  useTransition(batchId, dkConfirmEfDkSettlement);

export const useFinalConfirmEfDkSettlement = (batchId: string | undefined) =>
  useTransition(batchId, finalConfirmEfDkSettlement);

export const useLockEfDkSettlement = (batchId: string | undefined) =>
  useTransition(batchId, lockEfDkSettlement);

export const useReviseEfDkSettlement = (batchId: string | undefined) =>
  useTransition(batchId, reviseEfDkSettlement);
